import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { garagePinsApi } from '../../services/garagePinsApi';
import { GaragePinCandidate, GaragePinRow } from '../../types/garagePins';
import { ToastView, useToast } from '../promotions/useToast';
import GaragePinPicker from './GaragePinPicker';
import GaragePinRowItem from './GaragePinRowItem';

const DEFAULT_MAX = 20;

const renumber = (rows: GaragePinRow[]) => rows.map((r, i) => ({ ...r, position: i + 1 }));

/**
 * Admin → Configuration → Garage ordering (Spec 061 Flow A). Up to 20 garages pinned, in order,
 * above the customer app's Default ranking. Edits stay local until Save, which replaces the whole
 * list in one call; the next app request picks it up.
 */
export default function GarageOrderingTab() {
  const { token } = useAuth();
  const { toast, show } = useToast();
  const [saved, setSaved] = useState<GaragePinRow[]>([]);
  const [draft, setDraft] = useState<GaragePinRow[]>([]);
  const [max, setMax] = useState(DEFAULT_MAX);
  const [bookableGateOn, setBookableGateOn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    garagePinsApi
      .list(token)
      .then((res) => {
        setSaved(res.pins);
        setDraft(res.pins);
        setMax(res.max);
        setBookableGateOn(res.bookableGateOn);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const draftIds = useMemo(() => draft.map((r) => r.garageId), [draft]);
  const dirty = useMemo(
    () => draftIds.length !== saved.length || draftIds.some((id, i) => id !== saved[i].garageId),
    [draftIds, saved],
  );
  const full = draft.length >= max;

  const add = (c: GaragePinCandidate) => {
    if (full || draftIds.includes(c.garageId)) return;
    setSaveError(null);
    setDraft((prev) => [...prev, {
      position: prev.length + 1, garageId: c.garageId, name: c.name, city: c.city, status: 'APPROVED',
      blockedAt: null, visible: true, serviceless: c.serviceless, setBy: null, setAt: null,
    }]);
  };

  const move = (from: number, target: number) => setDraft((prev) => {
    const next = [...prev];
    const [row] = next.splice(from, 1);
    next.splice(target - 1, 0, row);
    return renumber(next);
  });

  const remove = (garageId: string) => setDraft((prev) => renumber(prev.filter((r) => r.garageId !== garageId)));

  const discard = () => {
    setDraft(saved);
    setSaveError(null);
  };

  const save = async () => {
    if (!token) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await garagePinsApi.replace(draftIds, token);
      setSaved(res.pins);
      setDraft(res.pins);
      setMax(res.max);
      setBookableGateOn(res.bookableGateOn);
      show('Saved — live on the next app request');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-loading">Loading…</div>;
  if (error) {
    return (
      <div>
        <div className="admin-error" role="alert" style={{ marginBottom: '0.75rem' }}>{error}</div>
        <button type="button" className="admin-btn admin-btn-outline" onClick={load}>Retry</button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', marginBottom: '0.75rem' }}>
        <p className="admin-page-subtitle">
          The first {draft.length} garages customers see, in this order. Everything after follows the Default ranking.
        </p>
        <span className="admin-text-muted" style={{ whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
          {draft.length} of {max} pinned
        </span>
      </div>

      <GaragePinPicker
        excludeIds={draftIds}
        disabled={full || saving}
        disabledHint={full ? `Maximum ${max} pinned garages` : undefined}
        onPick={add}
      />

      {dirty && (
        <div className="admin-info-banner warning" role="status">Unsaved changes — Save to apply them to the app.</div>
      )}

      {draft.length === 0 ? (
        <div className="admin-empty-state"><p>No garages pinned. The app shows the Default ranking.</p></div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: 110 }}>#</th>
                <th>Garage</th>
                <th>Status</th>
                <th>Pinned by / at</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {draft.map((row, i) => (
                <GaragePinRowItem
                  key={row.garageId}
                  row={row}
                  total={draft.length}
                  bookableGateOn={bookableGateOn}
                  disabled={saving}
                  onMove={(target) => move(i, target)}
                  onRemove={() => remove(row.garageId)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {saveError && <div className="admin-error" role="alert" style={{ marginTop: '0.75rem' }}>{saveError}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
        <button type="button" className="admin-btn admin-btn-outline" disabled={!dirty || saving} onClick={discard}>
          Discard
        </button>
        <button type="button" className="admin-btn admin-btn-primary" disabled={!dirty || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <ToastView toast={toast} />
    </div>
  );
}
